/**
 * IFRAP-AI.fr - Tutorial System
 * Interactive onboarding with step-by-step popups
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TutorialPopup = ({ step, onNext, onSkip, onClose, currentStep, totalSteps }) => {
  const [position, setPosition] = useState({ top: '50%', left: '50%' });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Position the popup relative to target element
    if (step.target === 'body' || step.position === 'center') {
      setPosition({ 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)' 
      });
    } else {
      const targetEl = document.querySelector(step.target);
      if (targetEl) {
        const rect = targetEl.getBoundingClientRect();
        const popupWidth = 320;
        const popupHeight = 200;
        
        let top, left;
        
        switch (step.position) {
          case 'right':
            top = rect.top + rect.height / 2 - popupHeight / 2;
            left = rect.right + 16;
            break;
          case 'left':
            top = rect.top + rect.height / 2 - popupHeight / 2;
            left = rect.left - popupWidth - 16;
            break;
          case 'top':
            top = rect.top - popupHeight - 16;
            left = rect.left + rect.width / 2 - popupWidth / 2;
            break;
          case 'bottom':
          default:
            top = rect.bottom + 16;
            left = rect.left + rect.width / 2 - popupWidth / 2;
            break;
        }
        
        // Keep within viewport
        top = Math.max(16, Math.min(window.innerHeight - popupHeight - 16, top));
        left = Math.max(16, Math.min(window.innerWidth - popupWidth - 16, left));
        
        setPosition({ top: `${top}px`, left: `${left}px` });
        
        // Highlight target element
        targetEl.style.position = 'relative';
        targetEl.style.zIndex = '1001';
        targetEl.style.boxShadow = '0 0 0 4px rgba(255, 176, 0, 0.5)';
        
        return () => {
          targetEl.style.position = '';
          targetEl.style.zIndex = '';
          targetEl.style.boxShadow = '';
        };
      }
    }
    
    // Animate in
    setTimeout(() => setVisible(true), 100);
  }, [step]);

  return createPortal(
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[1000]"
        onClick={onClose}
      />
      
      {/* Popup */}
      <div
        className={`fixed z-[1002] w-80 bg-[#0a0a0a] border border-[#FFB000] rounded-lg shadow-xl transition-all duration-300 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        style={position}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a]">
          <span className="text-[#FFB000] font-mono text-sm">
            Étape {currentStep + 1} / {totalSteps}
          </span>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4">
          <h3 className="text-white font-medium text-lg mb-2">
            {step.title}
          </h3>
          <p className="text-gray-400 text-sm leading-relaxed">
            {step.content}
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#1a1a1a]">
          <button
            onClick={onSkip}
            className="text-gray-500 hover:text-white text-sm transition-colors"
          >
            Passer le tutoriel
          </button>
          <button
            onClick={onNext}
            className="px-4 py-2 bg-[#FFB000] text-[#050505] font-medium text-sm rounded hover:bg-[#FFB000]/90 transition-colors"
          >
            {currentStep < totalSteps - 1 ? 'Suivant' : 'Terminer'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

const TutorialSystem = ({ onComplete }) => {
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    // Check if tutorial was already completed
    const completed = localStorage.getItem('ifrap_tutorial_completed');
    if (completed === 'true') {
      onComplete?.();
      return;
    }

    // Fetch tutorial steps
    const fetchTutorial = async () => {
      try {
        const token = localStorage.getItem('ifrap_token');
        const response = await fetch(`${API}/system/tutorial`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setSteps(data.steps);
          setIsActive(true);
        }
      } catch (err) {
        console.error('Tutorial fetch error:', err);
        onComplete?.();
      }
    };

    fetchTutorial();
  }, [onComplete]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Tutorial completed
      localStorage.setItem('ifrap_tutorial_completed', 'true');
      setIsActive(false);
      onComplete?.();
    }
  }, [currentStep, steps.length, onComplete]);

  const handleSkip = useCallback(() => {
    localStorage.setItem('ifrap_tutorial_completed', 'true');
    setIsActive(false);
    onComplete?.();
  }, [onComplete]);

  const handleClose = useCallback(() => {
    setIsActive(false);
    onComplete?.();
  }, [onComplete]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isActive) return;
      
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'Enter' || e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, handleNext, handleClose]);

  if (!isActive || steps.length === 0) {
    return null;
  }

  const currentStepData = steps[currentStep];

  return (
    <TutorialPopup
      step={currentStepData}
      onNext={handleNext}
      onSkip={handleSkip}
      onClose={handleClose}
      currentStep={currentStep}
      totalSteps={steps.length}
    />
  );
};

export default TutorialSystem;
